import {
  apiPaths,
  getInstutueApp,
  postInstituteAppData,
  putInstituteAppData,
  deleteInstituteAppData,
} from "modules/platform-components/api-integration-util";
import { getUserDetails } from "modules/platform-components/utils/shared-data.service";

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

// ---- Jobs ----
// Send metadata as multipart form fields (NOT URL query params) so the backend's
// @ModelAttribute binding matches the proven library/uploadTestQuestions pattern.
// Mixed @RequestParam + multipart was producing inconsistent behavior in prod.
export const createJob = async (
  file,
  { jobName, defaultSubjectId, testType, libraryName, classifySubject, classifyTopic, classifySubtopic }
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("jobName", jobName);
  if (defaultSubjectId) formData.append("defaultSubjectId", defaultSubjectId);
  if (testType) formData.append("testType", testType);
  if (libraryName) formData.append("libraryName", libraryName);
  // Booleans get serialised as strings in form-data; Spring's binder will coerce to Boolean.
  // Send only when explicitly set so backend defaults (testupload.default.classify.*) apply otherwise.
  if (classifySubject !== undefined) formData.append("classifySubject", classifySubject);
  if (classifyTopic !== undefined) formData.append("classifyTopic", classifyTopic);
  if (classifySubtopic !== undefined) formData.append("classifySubtopic", classifySubtopic);
  // Do NOT set Content-Type manually — axios/browser must set multipart/form-data
  // WITH the boundary for FormData uploads.
  return postInstituteAppData(apiPaths.testUploadJobs, {
    data: formData,
    headers: getHeaders(),
  });
};

export const listJobs = async (status) =>
  getInstutueApp(apiPaths.testUploadJobs, status ? { status } : {}, getHeaders());

export const getJob = async (jobId) =>
  getInstutueApp(`${apiPaths.testUploadJobs}/${jobId}`, {}, getHeaders());

export const getDraftQuestions = async (jobId) =>
  getInstutueApp(`${apiPaths.testUploadJobs}/${jobId}/questions`, {}, getHeaders());

export const approveJob = async (jobId) =>
  postInstituteAppData(`${apiPaths.testUploadJobs}/${jobId}/approve`, { headers: getHeaders() });

export const rejectJob = async (jobId) =>
  postInstituteAppData(`${apiPaths.testUploadJobs}/${jobId}/reject`, { headers: getHeaders() });

// Bulk apply subject / topic / subtopic to many drafts at once. Only the levels with a non-null
// id are written server-side; pass null/undefined to leave a level untouched.
export const bulkClassifyDrafts = async (jobId, { draftQuestionIds, subjectId, topicId, subtopicId }) =>
  postInstituteAppData(`${apiPaths.testUploadJobs}/${jobId}/bulk-classify`, {
    data: {
      draftQuestionIds,
      subjectId: subjectId || null,
      topicId: topicId || null,
      subtopicId: subtopicId || null,
    },
    headers: getHeaders(),
  });

// ---- Draft questions ----
export const updateDraftQuestion = async (draftQuestionId, payload) =>
  putInstituteAppData(`${apiPaths.testUploadQuestions}/${draftQuestionId}`, {
    data: payload,
    headers: getHeaders(),
  });

export const setReviewStatus = async (draftQuestionId, status) =>
  postInstituteAppData(`${apiPaths.testUploadQuestions}/${draftQuestionId}/review-status`, {
    params: { status },
    headers: getHeaders(),
  });

export const setUseExisting = async (draftQuestionId, useExisting) =>
  postInstituteAppData(`${apiPaths.testUploadQuestions}/${draftQuestionId}/use-existing`, {
    params: { useExisting },
    headers: getHeaders(),
  });

export const deleteDraftQuestion = async (draftQuestionId) =>
  deleteInstituteAppData(`${apiPaths.testUploadQuestions}/${draftQuestionId}`, { headers: getHeaders() });

// ---- Master data ----
export const getSubjects = async () =>
  getInstutueApp(apiPaths.getSubjects, {}, getHeaders());

// Cascading taxonomy lookups — reuse the existing classification-conflicts endpoints
// rather than introducing new ones. Both return { topics:[{id,name}] } / { subtopics:[{id,name}] }.
export const getTopicsBySubject = async (subjectId) =>
  getInstutueApp(apiPaths.topicsBySubject, { subjectId }, getHeaders());

export const getSubtopicsByTopic = async (topicId) =>
  getInstutueApp(apiPaths.subtopicsByTopic, { topicId }, getHeaders());

// Create-on-the-fly: lets faculty add a new topic/subtopic from the review page when
// the AI classifies into a subject/topic that doesn't have the right child yet.
// Backend dedups case-insensitively; result.result.created tells us whether a new row
// was actually inserted or an existing one was returned.
export const createTopic = async (subjectId, topicName) =>
  postInstituteAppData(`${apiPaths.testUploadJobs.replace("/jobs", "")}/taxonomy/topics`, {
    data: { subjectId, topicName },
    headers: getHeaders(),
  });

export const createSubtopic = async (topicId, subtopicName) =>
  postInstituteAppData(`${apiPaths.testUploadJobs.replace("/jobs", "")}/taxonomy/subtopics`, {
    data: { topicId, subtopicName },
    headers: getHeaders(),
  });
